import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { KadalLogo } from "@/components/KadalLogo";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";

function fmtErr(d) {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d?.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
}

/* ═══════════════ SLIDE DATA ═══════════════ */
const SLIDES = [
  {
    eyebrow: "AI Content Pipeline",
    heading: ["Your content library,", <br key="br" />, <em key="e" className="not-italic bg-clip-text text-transparent bg-gradient-to-r from-[#6baed6] via-[#4a9abf] to-[#1a6fc4]">finally</em>, " intelligent"],
    body: "Kadal transforms how you manage educational content. From raw files to fully enriched, compliant, derivative-ready assets at scale.",
    features: [
      { bold: "Universal ingestion", text: "across PDF, EPUB, SCORM, and more" },
      { bold: "Automated auditing", text: "surfaces accessibility, metadata, and standards gaps instantly" },
      { bold: "100s of AI agents", text: "assembled into custom agentic workflows with dynamic orchestration" },
      { bold: "Human-in-the-loop", text: "so every AI output is reviewed before it reaches learners" },
    ],
  },
  {
    eyebrow: "How it works",
    heading: ["Five steps from raw", <br key="br" />, "file to ", <em key="e" className="not-italic bg-clip-text text-transparent bg-gradient-to-r from-[#6baed6] via-[#4a9abf] to-[#1a6fc4]">enriched</em>, " asset"],
    body: "A progressive pipeline where each stage builds on the last, creating a cumulative intelligence layer over your entire library.",
    features: [
      { bold: "Ingest", text: "by connecting integrations or dropping files directly" },
      { bold: "Audit", text: "with content health scored across multiple dimensions" },
      { bold: "Enrich", text: "by running AI workflows across any selection of resources" },
      { bold: "Review", text: "and approve, edit, or reject with one click" },
      { bold: "Publish", text: "with a single click to Canvas, Brightspace, or your LMS" },
    ],
  },
  {
    eyebrow: "Built for every role",
    heading: ["Clarity for every", <br key="br" />, "person on ", <em key="e" className="not-italic bg-clip-text text-transparent bg-gradient-to-r from-[#6baed6] via-[#4a9abf] to-[#1a6fc4]">your team</em>],
    body: "Role-specific dashboards give administrators, content reviewers, and editors exactly what they need.",
    features: [
      { bold: "Admin dashboards", text: "with pipeline health, throughput, and compliance at a glance" },
      { bold: "Reviewer queues", text: "with clear to-do tasks, AI confidence scores, and one-click approval" },
      { bold: "Full inventory clarity", text: "across every asset, format, status, and gap in your library" },
      { bold: "Automated publishing", text: "sends approved content directly to your LMS in one click" },
    ],
  },
];

/* ═══════════════ RIGHT PANEL VISUALS ═══════════════ */

const FloatingCard = ({ className, children, delay = "0s" }) => (
  <div
    className={`absolute bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg border border-white/40 ${className}`}
    style={{ animation: `cardFloat 6s ease-in-out infinite ${delay}, cardFadeIn 0.8s ease-out ${delay} both` }}
  >
    {children}
  </div>
);

/* Slide 0: Metric cards */
const Visual0 = () => (
  <div className="relative w-[340px] h-[360px]">
    <FloatingCard className="top-0 left-0 px-5 py-4 w-[210px]" delay="0s">
      <div className="text-[10px] font-bold uppercase tracking-wider text-foreground/40 mb-1">Total Assets</div>
      <div className="text-2xl font-heading font-extrabold text-foreground">4,812</div>
      <div className="text-[11px] text-foreground/40 mt-0.5">47 titles across 3 verticals</div>
      <div className="mt-2.5 h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-[#014d7d] rounded-full" style={{ width: "88%" }} /></div>
    </FloatingCard>
    <FloatingCard className="top-[110px] right-0 px-5 py-4 w-[195px]" delay="0.3s">
      <div className="text-[10px] font-bold uppercase tracking-wider text-foreground/40 mb-1">Assessments Generated</div>
      <div className="text-2xl font-heading font-extrabold text-[#7dbd42]">965</div>
      <div className="text-[11px] text-foreground/40 mt-0.5">+89 this week</div>
      <div className="mt-2.5 h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-[#7dbd42] rounded-full" style={{ width: "72%" }} /></div>
    </FloatingCard>
    <FloatingCard className="bottom-0 left-[30px] px-5 py-4 w-[200px]" delay="0.6s">
      <div className="text-[10px] font-bold uppercase tracking-wider text-foreground/40 mb-1">Accessibility Score</div>
      <div className="text-2xl font-heading font-extrabold text-amber-600">69%</div>
      <div className="text-[11px] text-foreground/40 mt-0.5">+22pp after remediation</div>
      <div className="mt-2.5 h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-amber-500 rounded-full" style={{ width: "69%" }} /></div>
    </FloatingCard>
  </div>
);

/* Slide 1: Pipeline steps */
const Visual1 = () => (
  <div className="flex flex-col gap-3 w-[300px]">
    {[
      { n: "1", name: "Ingest", live: true },
      { n: "2", name: "Audit & Gap Analysis", live: true },
      { n: "3", name: "AI Workflow", live: true },
      { n: "4", name: "Human Review", live: true },
      { n: "5", name: "Export & Publish", live: false },
    ]?.map((s, i) => (
      <div
        key={s.n}
        className={`flex items-center gap-3 px-5 py-3.5 rounded-2xl backdrop-blur-xl border ${s.live ? "bg-white/90 border-white/40 shadow-lg" : "bg-white/40 border-white/20"}`}
        style={{ animation: `cardFadeIn 0.5s ease-out ${i * 0.12}s both` }}
      >
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-extrabold ${s.live ? "bg-[#014d7d] text-white" : "bg-white/60 text-foreground/50"}`}>{s.n}</div>
        <span className={`text-sm font-semibold flex-1 ${s.live ? "text-foreground" : "text-foreground/50"}`}>{s.name}</span>
        {s.live && <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-[#7dbd42]/15 text-[#7dbd42] border border-[#7dbd42]/30">Live</span>}
      </div>
    ))}
  </div>
);

/* Slide 2: Role grid */
const Visual2 = () => (
  <div className="grid grid-cols-2 gap-3 w-[320px]">
    {[
      { title: "Admin", desc: "Pipeline health, throughput & compliance", highlight: true },
      { title: "Review", desc: "Clear to-do queue with one-click approvals", highlight: true },
      { title: "4,812", desc: "Assets tracked across full inventory", highlight: false },
      { title: "1-click", desc: "Automated publishing to Canvas & Brightspace", highlight: false },
    ]?.map((c, i) => (
      <div
        key={c.title}
        className={`px-5 py-4 rounded-2xl backdrop-blur-xl border ${c.highlight ? "bg-white/90 border-white/40 shadow-lg" : "bg-white/40 border-white/20"}`}
        style={{ animation: `cardFadeIn 0.5s ease-out ${i * 0.1}s both` }}
      >
        <div className={`text-lg font-heading font-extrabold mb-1 ${c.highlight ? "text-[#014d7d]" : "text-foreground/70"}`}>{c.title}</div>
        <div className="text-[11px] text-foreground/50 leading-relaxed">{c.desc}</div>
      </div>
    ))}
  </div>
);

const VISUALS = [Visual0, Visual1, Visual2];

/* ═══════════════ COMPONENT ═══════════════ */
export const AuthPage = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [slide, setSlide] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const timerRef = useRef(null);
  const hoveringRef = useRef(false);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      if (!hoveringRef.current) setSlide((p) => (p + 1) % SLIDES.length);
    }, 5000);
  }, []);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  if (user) return <Navigate to="/" replace />;

  const goSlide = (dir) => {
    setSlide((p) => (p + dir + SLIDES.length) % SLIDES.length);
    startTimer();
  };

  const handleGoogleSignIn = async () => {
    setError(""); setLoading(true);
    try { await login("admin@kadal.ai", "admin123"); navigate("/"); }
    catch (e) { setError(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { await login(email, password); navigate("/"); }
    catch (err) { setError(fmtErr(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const s = SLIDES[slide];
  const Visual = VISUALS[slide];

  return (
    <div className="min-h-screen flex" data-testid="auth-page">

      {/* ══════ LEFT PANEL ══════ */}
      <div
        className="w-1/2 flex flex-col justify-between px-10 xl:px-16 py-10 bg-white relative z-10"
        onMouseEnter={() => { hoveringRef.current = true; }}
        onMouseLeave={() => { hoveringRef.current = false; }}
        data-testid="auth-left-panel"
      >
        {/* Logo */}
        <div className="mb-4 -ml-[25px]">
          <KadalLogo width={195} />
        </div>

        {/* Slide Content */}
        <div className="flex-1 flex flex-col justify-center max-w-lg" key={slide}>
          <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#014d7d]/60 mb-3" style={{ animation: "cardFadeIn 0.4s ease-out" }}>
            {s.eyebrow}
          </div>
          <h1
            className="font-heading font-extrabold text-3xl xl:text-4xl tracking-tight text-foreground leading-[1.15] mb-5"
            style={{ animation: "cardFadeIn 0.4s ease-out 0.05s both" }}
            data-testid="auth-heading"
          >
            {s.heading}
          </h1>
          <p className="text-[15px] text-foreground/55 leading-[1.7] mb-7 max-w-sm" style={{ animation: "cardFadeIn 0.4s ease-out 0.1s both" }}>
            {s.body}
          </p>
          <div className="space-y-2.5 mb-8" style={{ animation: "cardFadeIn 0.4s ease-out 0.15s both" }}>
            {s.features?.map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#7dbd42] mt-[7px] shrink-0" />
                <p className="text-sm text-foreground/60 leading-relaxed">
                  <span className="font-semibold text-foreground/85">{f.bold}</span> {f.text}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom: Nav dots + arrows + Sign in */}
        <div>
          {/* Slide navigation */}
          <div className="flex items-center gap-4 mb-6">
            <button onClick={() => goSlide(-1)} className="w-8 h-8 rounded-full border border-border/60 flex items-center justify-center text-foreground/50 hover:text-foreground hover:border-foreground/30" data-testid="slide-prev">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex gap-2">
              {SLIDES?.map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setSlide(i); startTimer(); }}
                  className={`h-1.5 rounded-full ${i === slide ? "w-6 bg-[#014d7d]" : "w-1.5 bg-foreground/15 hover:bg-foreground/30"}`}
                  style={{ transition: "all 0.3s" }}
                  data-testid={`slide-dot-${i}`}
                />
              ))}
            </div>
            <button onClick={() => goSlide(1)} className="w-8 h-8 rounded-full border border-border/60 flex items-center justify-center text-foreground/50 hover:text-foreground hover:border-foreground/30" data-testid="slide-next">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm text-center" data-testid="auth-error">{error}</div>
          )}

          {/* Google Sign-In */}
          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full max-w-sm h-12 rounded-xl bg-[#1a1a2e] border border-[#1a1a2e] text-white text-sm font-semibold flex items-center justify-center gap-3 hover:bg-[#0f0f1e] hover:shadow-soft disabled:opacity-50 active:scale-[0.98]"
            data-testid="google-signin-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#8ab4f8"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#81c995"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#fdd663"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#ea8600"/>
                </svg>
                Sign in with Google
              </>
            )}
          </button>

          {!showEmailForm && (
            <button onClick={() => setShowEmailForm(true)} className="text-xs text-foreground/45 hover:text-foreground/50 mt-3 max-w-sm text-center block" data-testid="show-email-form">
              or sign in with email
            </button>
          )}

          {showEmailForm && (
            <div className="max-w-sm mt-4 animate-float-up">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 h-px bg-border/60" /><span className="text-xs text-foreground/45">or</span><div className="flex-1 h-px bg-border/60" />
              </div>
              <form onSubmit={handleEmailSubmit} className="space-y-2.5">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full h-11 px-4 text-sm border border-border/60 rounded-xl bg-white outline-none focus:ring-2 focus:ring-[#014d7d]/10 focus:border-[#014d7d]/40 placeholder:text-foreground/45" data-testid="auth-email" placeholder="Email" />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full h-11 px-4 text-sm border border-border/60 rounded-xl bg-white outline-none focus:ring-2 focus:ring-[#014d7d]/10 focus:border-[#014d7d]/40 placeholder:text-foreground/45" data-testid="auth-password" placeholder="Password" />
                <button type="submit" disabled={loading} className="w-full h-11 rounded-xl bg-[#014d7d] text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#013d66] disabled:opacity-50 active:scale-[0.98]" data-testid="auth-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
                </button>
              </form>
            </div>
          )}

          <p className="text-[10px] text-foreground/15 mt-5 max-w-sm">By continuing, you agree to Kadal AI's Terms of Service and Privacy Policy.</p>
        </div>
      </div>

      {/* ══════ RIGHT PANEL — Animated gradient ══════ */}
      <div className="w-1/2 relative overflow-hidden flex items-center justify-center" data-testid="auth-visual-panel">
        {/* Gradient background — pipeline.html blue→teal→green */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, rgb(23 111 163) 0%, rgb(40 156 209) 18%, rgb(58, 143, 170) 32%, rgb(45, 125, 149) 46%, rgb(37, 107, 122) 58%, rgb(30, 138, 110) 72%, rgb(35 171 113) 85%, rgb(8 103 59) 100%)" }} />

        {/* Animated orbs for depth */}
        <div className="absolute w-[520px] h-[520px] rounded-full -top-[120px] -right-[100px]" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.22) 0%, rgba(180,230,255,0.10) 45%, transparent 70%)", animation: "orbFloat 12s ease-in-out infinite" }} />
        <div className="absolute w-[400px] h-[400px] rounded-full -bottom-[80px] -left-[60px]" style={{ background: "radial-gradient(circle, rgba(150,255,200,0.18) 0%, rgba(80,220,160,0.08) 50%, transparent 70%)", animation: "orbFloat 15s ease-in-out infinite 3s" }} />
        <div className="absolute w-[280px] h-[280px] rounded-full top-[40%] right-[10%]" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 65%)", animation: "orbFloat 9s ease-in-out infinite 1.5s" }} />

        {/* Concentric arc rings */}
        <div className="absolute w-[700px] h-[700px] rounded-full border border-white/[0.1] -bottom-[200px] -left-[200px]" />
        <div className="absolute w-[500px] h-[500px] rounded-full border border-white/[0.1] -bottom-[100px] -left-[100px]" />
        <div className="absolute w-[300px] h-[300px] rounded-full border border-white/[0.1] bottom-0 left-0" />

        {/* Slide visual */}
        <div className="relative z-10" key={slide}>
          <Visual />
        </div>
      </div>
    </div>
  );
};
